import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  unminimizeCommentMutation,
  unminimizeCommentMutation$rawResponse,
} from './__generated__/unminimizeCommentMutation.graphql'

export function unminimizeCommentMutation({
  environment,
  input: {id},
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {id: string}
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  const optimisticResponse: unminimizeCommentMutation$rawResponse = {
    unminimizeComment: {
      clientMutationId: '',
      unminimizedComment: {
        id,
        __typename: 'IssueComment',
        __isNode: 'IssueComment',
        isMinimized: false,
        minimizedReason: null,
        pendingMinimizeReason: null,
      },
    },
  }

  const optimistic = publishOptimisticFields(id, {isMinimized: false, minimizedReason: null})

  let disposable
  try {
    disposable = commitMutation<unminimizeCommentMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation unminimizeCommentMutation($input: UnminimizeCommentInput!) @raw_response_type {
          unminimizeComment(input: $input) {
            clientMutationId
            unminimizedComment {
              isMinimized
              minimizedReason
              ... on IssueComment {
                id
                pendingMinimizeReason
              }
            }
          }
        }
      `,
      variables: {
        input: {subjectId: id},
      },
      optimisticResponse,
      onError: error => {
        optimistic.failed(error)
        onError?.(error)
      },
      onUnsubscribe: () => optimistic.failed(new Error('Unminimizing the comment was cancelled')),
      onCompleted: response => {
        const unminimized = response.unminimizeComment?.unminimizedComment
        if (!unminimized) {
          const error = new Error('unminimizeComment returned no payload')
          optimistic.failed(error)
          onError?.(error)
          return
        }
        optimistic.completed({
          isMinimized: unminimized.isMinimized,
          minimizedReason: unminimized.minimizedReason,
        })
        onCompleted?.()
      },
    })
  } catch (error) {
    optimistic.failed(error instanceof Error ? error : new Error('Failed to unminimize comment'))
    throw error
  }

  return disposable
}
